// Ubicación en el repo: src/components/PatientAttachments.jsx
// "Documentos adjuntos" de la ficha de paciente — mismo contenido que
// patientAttachListHTML()/addPatientAttachment() del prototipo (línea
// ~1634-1676): un input de archivo que sube apenas se elige (sin botón
// aparte), lista con nombre/fecha, "Ver"/"Eliminar" por archivo. A
// diferencia del prototipo (que guardaba el archivo como base64 en la fila
// del paciente), acá sube el archivo real al bucket privado
// `patient-attachments` (ver DECISIONS.md #20) y "Ver" pide una signed URL
// de corta duración, porque el bucket no es público.
import { useEffect, useRef, useState } from 'react';
import {
  listPatientAttachments,
  uploadPatientAttachment,
  getAttachmentUrl,
  softDeletePatientAttachment,
} from '../lib/patientAttachments';
import { formatDate } from '../lib/format';
import Spinner from './Spinner';

export default function PatientAttachments({ patientId, actorId }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  // Ronda estética (24-ago-2026): el `<input type="file">` nativo se ve
  // fuera de lugar al lado de los botones ya estilizados del resto de la
  // ficha — se oculta y se dispara por código desde un botón normal, mismo
  // patrón usado para subir logos/adjuntos en muchos sitios.
  const fileInputRef = useRef(null);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      setAttachments(await listPatientAttachments(patientId));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los documentos adjuntos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      await uploadPatientAttachment(patientId, file, actorId);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo subir el documento.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleView(att) {
    setError('');
    try {
      const url = await getAttachmentUrl(att.storage_path);
      window.open(url, '_blank');
    } catch (err) {
      setError(err.message || 'No se pudo abrir el documento.');
    }
  }

  async function handleDelete(att) {
    const ok = window.confirm(`¿Eliminar "${att.name}"?`);
    if (!ok) return;
    setBusyId(att.id);
    setError('');
    try {
      await softDeletePatientAttachment(att.id, actorId);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el documento.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="detail-section">
      <h2>Documentos adjuntos</h2>

      {error && <p className="auth-error">{error}</p>}

      {loading ? (
        <Spinner />
      ) : attachments.length === 0 ? (
        <p className="text-muted">Sin documentos adjuntos.</p>
      ) : (
        <div>
          {attachments.map((att) => (
            <div className="apt-item" key={att.id}>
              <div className="apt-info">
                <div className="name">{att.name}</div>
                <div className="meta">{formatDate(att.uploaded_at)}</div>
              </div>
              <button type="button" className="link-button" onClick={() => handleView(att)}>
                Ver
              </button>
              <button
                type="button"
                className="link-button link-button--danger"
                disabled={busyId === att.id}
                onClick={() => handleDelete(att)}
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="apt-upload">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={handleFileChange}
          disabled={uploading}
          hidden
        />
        <button
          type="button"
          className="button-secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Spinner inline label="Subiendo…" /> : '📎 Agregar documento'}
        </button>
      </div>
    </section>
  );
}
